import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { SubscriptionCancelReason, subscriptionCancelReasonLabels } from 'shared';

import { Button } from '../../../common/Atoms/Button';
import { Divider } from '../../../common/Atoms/Divider';
import { Modal } from '../../../common/Atoms/Modal';
import { RadioStack } from '../../../common/Atoms/RadioStack';
import { TextArea } from '../../../common/Atoms/TextArea';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';

interface ICancelSubscriptionModalProps {
  show: boolean;
  onConfirm: (reason: SubscriptionCancelReason, comment: string) => void;
  onCancel: () => void;
  confirmLoading?: boolean;
}

/** Fisher-Yates shuffle so no reason gets the top-of-list bias. "Prefer not to say" stays first, "Other" last. */
export function shuffledReasonOptions() {
  const pinned = [SubscriptionCancelReason.PreferNotToSay, SubscriptionCancelReason.Other];
  const reasons = Object.values(SubscriptionCancelReason).filter(r => !pinned.includes(r));

  for (let i = reasons.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [reasons[i], reasons[j]] = [reasons[j], reasons[i]];
  }

  return [SubscriptionCancelReason.PreferNotToSay, ...reasons, SubscriptionCancelReason.Other].map(value => ({
    value,
    title: subscriptionCancelReasonLabels[value],
  }));
}

export function CancelSubscriptionModal({
  show,
  onConfirm,
  onCancel,
  confirmLoading,
}: ICancelSubscriptionModalProps) {
  const [reason, setReason] = useState<SubscriptionCancelReason | null>(null);
  const [comment, setComment] = useState(``);
  // Reshuffle and clear the form each time the modal opens
  const options = useMemo(() => shuffledReasonOptions(), [show]);

  useEffect(() => {
    setReason(null);
    setComment(``);
  }, [show]);

  return (
    <Modal
      setOpen={ onCancel }
      open={ show }
      title={ `Cancel subscription` }
      icon={ <ExclamationTriangleIcon className={ `text-orange-500 h-5 w-5` } /> }
    >
      <div>
        <Divider
          weight={ `light` }
          spacing={ `small` }
        />

        <Paragraph variant={ `primary` }>
          { `Your subscription ends straight away. Reminders, late fees and other automations will stop running for your linked organisations.` }
        </Paragraph>

        <div className={ `mt-4` }>
          <RadioStack
            label={ `Why are you cancelling?` }
            options={ options }
            selected={ reason }
            onChange={ e => setReason(e.value as SubscriptionCancelReason) }
          />
        </div>

        <TextArea
          className={ `mt-4` }
          label={ `Anything else you'd like to tell us? (optional)` }
          value={ comment }
          onChange={ e => setComment(String(e.value)) }
          rows={ 3 }
        />

        <div className={ `mt-4 flex justify-center space-x-4` }>
          <Button
            color={ `red` }
            onClick={ () => onConfirm(reason, comment.trim()) }
            loading={ confirmLoading }
            disabled={ !reason || confirmLoading }
          >
            { `Cancel subscription` }
          </Button>

          <Button
            color={ `gray` }
            onClick={ onCancel }
            disabled={ confirmLoading }
          >
            { `Keep subscription` }
          </Button>
        </div>
      </div>
    </Modal>
  );
}
